import React from 'react'
import { Typography, Grid } from '@the-platform-group/component-library'
import { Button } from '@the-platform-group/component-library/Button'
import { getFullName } from '@the-platform-group/formatters/people'
import { usePeopleMergeContext } from 'pages/MergeProfiles/contexts/PeopleMergeContext'
import {
  getJobTitleAndOrgName,
  getJobFieldValue,
  getJobOverrideValue,
} from 'pages/MergeProfiles/utils'

import JobDetailsTable from '../JobDetailsTable'
import BaseJobDrawer from './BaseJobDrawer'

const PREVIEW_FIELDS = [
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'active', label: 'Job Status' },
  { key: 'organization', label: 'Organization' },
  { key: 'address', label: 'Address' },
  { key: 'activities', label: 'Activities' },
]

/**
 * JobMappedPreviewDrawer - Preview of mapped jobs during the job mapping step
 *
 * This drawer shows a preview of a mapped job
 * It displays:
 * - FactSet job information
 * - Mapped Platform jobs with override selections
 * - Preview data showing how the merged result will look
 * - Actions: Edit mapping or Delete mapping
 *
 * **Opened by:**
 * - Clicking "View" button on a mapped job in the FactSetJobsList
 * @param {Object} props
 * @param {boolean} props.open - Controls drawer visibility
 * @param {Function} props.onClose - Callback to close the drawer
 * @param {string} props.selectedTargetJobId - The FactSet job ID to display
 * @param {Function} props.onEdit - Callback to open JobMapDrawer for editing
 *
 * @returns {React.Component} The mapped job preview drawer or null if no job is selected
 */
const JobMappedPreviewDrawer = ({ open, onClose, selectedTargetJobId, onEdit }) => {
  const { mergeData, jobMappingData } = usePeopleMergeContext()
  const { sourceJobs, targetJobs, targetPerson, preview } = mergeData
  const { getMappedSourceJobsForTarget, removeMappingsForTarget } = jobMappingData

  const selectedTargetJob = selectedTargetJobId
    ? targetJobs.find(job => job.id === selectedTargetJobId)
    : null

  const mappedSourceJobs = getMappedSourceJobsForTarget(selectedTargetJobId, sourceJobs)

  const handleEdit = () => {
    if (mappedSourceJobs.length > 0 && selectedTargetJobId) {
      const sourceJobIds = mappedSourceJobs.map(job => job.id)
      onEdit(sourceJobIds, selectedTargetJobId)
    }
  }

  const handleDelete = () => {
    removeMappingsForTarget(selectedTargetJobId)
    onClose()
  }

  if (!selectedTargetJob) {
    return null
  }

  const footer = (
    <Grid container justifyContent="space-between">
      <Grid item xs={6}>
        <Button variant="outlined" color="error" onClick={handleDelete}>
          Delete
        </Button>
      </Grid>
      <Grid item xs={6} container justifyContent="flex-end" spacing={1}>
        <Grid item>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleEdit}>
            Edit
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )

  return (
    <BaseJobDrawer open={open} onClose={onClose} title="Mapped Job Preview" footer={footer}>
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Typography variant="body1" fontWeight="bold">
            {getFullName(targetPerson)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            FactSet Job: {getJobTitleAndOrgName(selectedTargetJob)}
          </Typography>
        </Grid>

        <Grid item>
          <JobDetailsTable
            fields={PREVIEW_FIELDS}
            selectedTargetJob={selectedTargetJob}
            getFieldValue={(job, fieldKey) =>
              getJobFieldValue(job, fieldKey, { preview, isSource: false })
            }
            getOverrideValue={fieldKey => getJobOverrideValue(mappedSourceJobs, fieldKey)}
          />
        </Grid>
      </Grid>
    </BaseJobDrawer>
  )
}

export default JobMappedPreviewDrawer
